/**
 * Shared skeleton placeholders for XEO detail / list views.
 *
 * Use these instead of a lone centered <CircularProgress> so a page keeps its
 * shape while data loads (same idea as the dashboard stat-count skeletons and
 * <TableSkeleton>). All match the app's soft card style (1px #e6eaf2, radius 2.5)
 * and ease in gently so swapping loader → content doesn't blink.
 */
import React from 'react';
import { Box, Grid, Paper, Skeleton, Stack } from '@mui/material';

const FADE_IN = {
    animation: 'skelFade 320ms cubic-bezier(0.22,1,0.36,1)',
    '@keyframes skelFade': { from: { opacity: 0 }, to: { opacity: 1 } },
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
};

// A single card placeholder: header (icon + title/subtitle) then a few text lines.
export const CardSkeleton = ({ lines = 4, height }) => (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid var(--line)', height: height || '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
            <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: 1.5, flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton variant="text" width="45%" height={20} />
                <Skeleton variant="text" width="65%" height={14} />
            </Box>
        </Box>
        <Stack spacing={1.2}>
            {Array.from({ length: lines }).map((_, i) => (
                <Skeleton key={i} variant="text" height={16} width={`${92 - i * 9}%`} />
            ))}
        </Stack>
    </Paper>
);

/**
 * DetailSkeleton — generic detail-page body: a grid of section-card placeholders
 * that mirrors the PageHeader + SectionCard grid used by the job/site detail
 * pages. Drop it in where the loaded content would render.
 */
export const DetailSkeleton = ({ sx }) => (
    <Grid container spacing={2} alignItems="stretch" sx={{ ...FADE_IN, ...sx }}>
        <Grid item xs={12} md={8}><CardSkeleton lines={5} height={280} /></Grid>
        <Grid item xs={12} md={4}><CardSkeleton lines={3} height={280} /></Grid>
        <Grid item xs={12} md={6}><CardSkeleton lines={4} height={220} /></Grid>
        <Grid item xs={12} md={6}><CardSkeleton lines={4} height={220} /></Grid>
    </Grid>
);

/**
 * RowsSkeleton — a stack of full-width row bars for list/log placeholders where a
 * full <table> skeleton would be overkill. `rows` bars inside a bordered card.
 */
export const RowsSkeleton = ({ rows = 6, height = 44, bordered = true }) => (
    <Box sx={{ ...FADE_IN, ...(bordered ? { border: '1px solid var(--line)', borderRadius: 2, p: 1 } : {}) }}>
        <Stack spacing={1}>
            {Array.from({ length: rows }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={height} sx={{ borderRadius: 1.5 }} />
            ))}
        </Stack>
    </Box>
);

/**
 * TileCardsSkeleton — placeholder for a responsive grid of small summary cards
 * (a status chip + date, a two-line description, an action button). Mirrors that
 * markup exactly rather than approximating it, because the point is for the grid
 * to be the SAME HEIGHT loaded and unloaded: a section that renders only a
 * heading while it waits, then expands into two rows of cards, drags everything
 * below it down the page the moment the fetch lands.
 *
 * `count` should be how many cards you expect, not a round number — see
 * rememberCount() for keeping that honest between visits.
 */
// One minimum height shared by the placeholder and the real card. Without it the
// card's height follows its description text — a one-line prompt is ~19px shorter
// than a two-line one — so the placeholder can only ever approximate it, and the
// rows come out ragged besides. Both sides use these, so the swap is exact.
export const TILE_MIN_H = 132;
export const TILE_MIN_H_3 = 152;

export const TileCardsSkeleton = ({ count = 3, footer = false, spacing = 1.5, lines = 2, minHeight }) => (
    <Box sx={FADE_IN}>
        <Grid container spacing={spacing}>
            {Array.from({ length: count }).map((_, i) => (
                <Grid item xs={12} sm={6} md={4} key={i} sx={{ display: 'flex' }}>
                    <Paper elevation={0} sx={{
                        border: '1px solid var(--line)', borderRadius: 2, height: '100%',
                        minHeight: minHeight || (lines > 2 ? TILE_MIN_H_3 : TILE_MIN_H),
                    }}>
                        <Box sx={{ p: 1.75, height: '100%', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Skeleton variant="rounded" width={62} height={18} sx={{ borderRadius: 999 }} />
                                <Skeleton variant="text" width={70} height={12} />
                            </Box>
                            {Array.from({ length: lines }).map((_, k) => (
                                <Skeleton key={k} variant="text" height={17} width={k === lines - 1 ? '72%' : '96%'} />
                            ))}
                            <Skeleton variant="rounded" height={28} sx={{ borderRadius: 1.5, mt: 'auto' }} />
                        </Box>
                    </Paper>
                </Grid>
            ))}
        </Grid>
        {footer && (
            <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Skeleton variant="rounded" width={168} height={31} sx={{ borderRadius: 2, mx: 'auto' }} />
            </Box>
        )}
    </Box>
);

/**
 * How many placeholders to draw, remembered from the last successful load.
 *
 * A fixed guess is wrong in one direction or the other: too few and the section
 * grows when data lands, too many and it shrinks. The count from last time is
 * almost always right, because these lists barely change between visits. Falls
 * back to `fallback` on a first visit or if storage is unavailable (private
 * window, blocked site data — the accessor itself can throw).
 */
export const rememberCount = {
    read(key, fallback = 3, max = 12) {
        try {
            const n = parseInt(window.localStorage.getItem(`xeo.count.${key}`), 10);
            if (Number.isFinite(n) && n > 0) return Math.min(n, max);
        } catch (_) { /* no storage — use the fallback */ }
        return fallback;
    },
    write(key, n) {
        try {
            if (Number.isFinite(n) && n >= 0) window.localStorage.setItem(`xeo.count.${key}`, String(n));
        } catch (_) { /* nothing to do */ }
    },
};

/**
 * FormSkeleton — placeholder for the two-column <FormShell> used by every CRUD
 * edit/duplicate page. Those pages did `if (loading) return <Loader />`, which
 * swapped a 100vh centred spinner for the form: the whole page changed height
 * twice (once into the spinner, once into the form) and the pane grew a
 * scrollbar it didn't need.
 *
 * Mirrors the shell's geometry — explainer column, same border and radius, a
 * footer action bar — so the form simply resolves in place. `railWidth`/`navItems`
 * cover the SettingsFormShell variant, which has a narrower column carrying a
 * section nav.
 */
export const FormSkeleton = ({ fields = 5, railWidth = 300, navItems = 0 }) => (
    <Box sx={{ px: { xs: 0, md: 1 }, pb: 8, pt: 1, mt: 1, ...FADE_IN }}>
        <Paper elevation={0} sx={{
            width: '100%', border: '1px solid var(--line)', borderRadius: 3, overflow: 'hidden',
            display: 'flex', flexDirection: { xs: 'column', md: 'row' },
        }}>
            {/* Left — explainer */}
            <Box sx={{
                width: { xs: 'auto', md: railWidth }, flexShrink: 0, p: { xs: 2.5, md: 3 },
                bgcolor: 'var(--surface-2)',
                borderRight: { xs: 'none', md: '1px solid var(--line)' },
            }}>
                <Skeleton variant="rounded" width={48} height={48} sx={{ borderRadius: 2.5, mb: 2 }} />
                <Skeleton variant="text" width="70%" height={26} />
                <Skeleton variant="text" width="94%" height={15} sx={{ mt: 1 }} />
                <Skeleton variant="text" width="80%" height={15} />
                {/* SettingsFormShell puts a section nav in this column. Its rows
                    are 48px, and a capability row's switch grows to medium on a
                    touch screen, making it 56px. The first row is Details, which
                    has no switch. Heights go in sx, not the height prop: that one
                    is an inline style, which a media query can't override. */}
                {navItems > 0 && (
                    <Box sx={{ mt: 2.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {Array.from({ length: navItems }).map((_, i) => (
                            <Skeleton key={i} variant="rounded" sx={{ borderRadius: 2, height: 48, ...(i > 0 && { '@media (pointer: coarse)': { height: 56 } }) }} />
                        ))}
                    </Box>
                )}
            </Box>
            {/* Right — fields, then the action bar */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ p: { xs: 2.5, md: 3 }, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {Array.from({ length: fields }).map((_, i) => (
                        <Skeleton key={i} variant="rounded" height={56} sx={{ borderRadius: 2 }} />
                    ))}
                </Box>
                <Box sx={{
                    borderTop: '1px solid var(--line)', px: { xs: 2.5, md: 3 }, py: 2,
                    display: 'flex', justifyContent: 'flex-end', gap: 1.5,
                }}>
                    <Skeleton variant="rounded" width={92} height={38} sx={{ borderRadius: 2 }} />
                    <Skeleton variant="rounded" width={132} height={38} sx={{ borderRadius: 2 }} />
                </Box>
            </Box>
        </Paper>
    </Box>
);

/**
 * JobTileCardsSkeleton — placeholder for the "Recent Writing Jobs" card, which has a
 * distinctly different shape from the simpler tile above: a type badge and status
 * chip on one row, then an icon tile beside a title and a small uppercase tagline,
 * then sub-keyword chips, then a full-width action button.
 *
 * It gets its own component because the generic TileCardsSkeleton was both the wrong
 * SIZE and the wrong SHAPE for it — three plain text lines at 152px against a real
 * card that measures ~176 and is mostly chips and a button. A placeholder that
 * doesn't match makes the grid resize the moment data lands, which is the whole
 * thing these exist to prevent.
 */
export const JOB_TILE_MIN_H = 176;

export const JobTileCardsSkeleton = ({ count = 3, footer = false }) => (
    <Box sx={FADE_IN}>
        <Grid container spacing={2}>
            {Array.from({ length: count }).map((_, i) => (
                <Grid item xs={12} sm={6} md={4} key={i} sx={{ display: 'flex' }}>
                    <Paper elevation={0} sx={{
                        width: '100%', minHeight: JOB_TILE_MIN_H,
                        border: '1px solid var(--line)', borderRadius: 2.5,
                        display: 'flex', flexDirection: 'column',
                    }}>
                        <Box sx={{ p: 1.75, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                            {/* type badge · date + status chip */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                <Skeleton variant="rounded" width={54} height={17} sx={{ borderRadius: 1 }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Skeleton variant="text" width={58} height={11} />
                                    <Skeleton variant="rounded" width={62} height={18} sx={{ borderRadius: 999 }} />
                                </Box>
                            </Box>
                            {/* icon tile + title + tagline */}
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.25 }}>
                                <Skeleton variant="rounded" width={34} height={34} sx={{ borderRadius: '9px', flexShrink: 0 }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Skeleton variant="text" width="82%" height={19} />
                                    <Skeleton variant="text" width="46%" height={11} />
                                </Box>
                            </Box>
                            {/* sub-keyword chips */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flexGrow: 1, alignContent: 'flex-start', mb: 1.25 }}>
                                {[64, 48, 72].map((w, k) => (
                                    <Skeleton key={k} variant="rounded" width={w} height={20} sx={{ borderRadius: 999 }} />
                                ))}
                            </Box>
                            <Skeleton variant="rounded" height={31} sx={{ borderRadius: 1.5, mt: 'auto' }} />
                        </Box>
                    </Paper>
                </Grid>
            ))}
        </Grid>
        {footer && (
            <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Skeleton variant="rounded" width={176} height={31} sx={{ borderRadius: 2, mx: 'auto' }} />
            </Box>
        )}
    </Box>
);
